<script>
import VsTextInput from '@/components/vision/Inputs/VsTextInput'
import VsWrapper from '@/components/vision/VsWrapper'
import VsHeading from '@/components/vision/VsHeading'
import VsButton from '@/components/vision/VsButton'
import * as fb from '@ironsight/form-builder'
import { mapGetters } from 'vuex'

export default {
  name: 'CoreFieldSettings',
  components: { VsButton, VsHeading, VsTextInput, VsWrapper },

  props: {
    field: {
      type: Object,
      default: () => {},
    },
    builder: {
      type: Object,
      required: false,
      default: () => {},
    },
    isRequiredToggleVisible: {
      type: Boolean,
      default: true,
    },
    isEditingCustomFields: {
      type: Boolean,
      default: false,
    },
    isNestedField: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters('permissions', ['isGlobalAdmin']),
    fieldCanHaveValue() {
      return ![
        fb.HeadingFieldDefinition,
        fb.ArrayFieldDefinition,
        fb.MarkdownFieldDefinition,
        fb.ObjectFieldDefinition,
        fb.FormButtonsDefinition,
        fb.ReferenceField,
      ].some((field) => this.field instanceof field)
    },
    isTitleAndDescriptionVisible() {
      return ![fb.MarkdownFieldDefinition, fb.ReferenceField].some(
        (field) => this.field instanceof field,
      )
    },
    hideRequiredToggle() {
      return !this.fieldCanHaveValue || !this.isRequiredToggleVisible
    },
    isRequiredToggleDisabled() {
      return [fb.SubjectFieldDefinition, fb.SubjectLocationFieldDefinition].some(
        (field) => this.field instanceof field,
      )
    },
    hasDependencies() {
      return this.builder
        ? this.builder.immediateConstDependenciesOfField(this.field).length > 0
        : false
    },
    shouldShowClearValueOnCloneToggle() {
      return this.isEditingCustomFields && this.fieldCanHaveValue
    },
    shouldShowSortableToggle() {
      return (
        [
          fb.NumberFieldDefinition,
          fb.BooleanFieldDefinition,
          fb.EnumStringFieldDefinition,
          fb.TextEntryFieldDefinition,
          fb.CreateableSelectorDefinition,
          fb.GenericSelectorDefinition,
          fb.DateFieldDefinition,
        ].some((field) => this.field instanceof field) &&
        this.isEditingCustomFields &&
        this.isGlobalAdmin &&
        !this.isNestedField
      )
    },
  },
  methods: {
    updateTitle(title) {
      this.emitChanges('title', title)
    },
    updateDescription(description) {
      this.emitChanges('description', description)
    },
    updateFieldIsRequired(value) {
      this.emitChanges('isRequired', value !== null)
    },
    updateClearValueOnClone(value) {
      this.emitChanges('clearValueOnClone', Boolean(value))
    },
    updateSortable(value) {
      this.emitChanges('sortable', Boolean(value))
    },
    removeField() {
      this.$emit('remove-field', this.field)
    },
    emitChanges(propertyKey, value) {
      this.$emit('modify-field', {
        propertyKey,
        fieldValue: value,
        key: this.field.uniqueKey(),
      })
    },
  },
}
</script>

<template>
  <vs-wrapper space="small">
    <div class="mb-5 d-flex justify-space-between align-center">
      <vs-heading>{{ field.fieldTypeTitle }} Properties</vs-heading>
      <vs-button
        :class="{ 'greyed-out': hasDependencies }"
        :disabled="hasDependencies"
        destructive
        label="Delete Field"
        type="secondary"
        @click="removeField(field)"
      />
    </div>
    <vs-wrapper v-if="isTitleAndDescriptionVisible" space="small">
      <vs-text-input
        v-model.lazy="field.title"
        :value="field.title"
        label="Title"
        @input="updateTitle"
      />
      <vs-text-input
        v-model.lazy="field.description"
        :value="field.description"
        label="Description"
        @input="updateDescription"
      />
    </vs-wrapper>
    <v-row class="mt-1">
      <v-switch
        v-if="!hideRequiredToggle"
        :disabled="isRequiredToggleDisabled"
        :input-value="field.isRequired"
        :value="field.isRequired"
        class="px-4"
        inset
        label="Required"
        @change="updateFieldIsRequired"
      ></v-switch>
      <v-switch
        v-if="shouldShowClearValueOnCloneToggle"
        :input-value="field.clearValueOnClone"
        :value="field.clearValueOnClone"
        class="px-4"
        inset
        label="Clear value on clone"
        @change="updateClearValueOnClone"
      />
      <v-tooltip v-if="shouldShowSortableToggle" bottom max-width="325">
        <template #activator="{ on, attrs }">
          <div v-bind="attrs" v-on="on">
            <v-switch
              :input-value="field.sortable"
              :value="field.sortable"
              class="px-4"
              inset
              label="Sortable"
              @change="updateSortable"
            />
          </div>
        </template>
        Toggling this will affect all fields with matching keys in the hub.<br />This does not
        result in a new schema version.
      </v-tooltip>
    </v-row>
  </vs-wrapper>
</template>

<style scoped>
.greyed-out {
  opacity: 0.5;
  pointer-events: none;
}
</style>
